import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '../components/Icon.js'
import { Button } from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { SpotlightFrame, useTarget } from './Spotlight.js'
import type { TourStep } from './types.js'

/**
 * The welcome tour, one step at a time. Decision 115.
 *
 * Each step opens its screen behind the card and lights up where it lives on
 * the rail or the tabs. The arrow keys and Enter move it; Escape asks how to
 * stop. Keys are taken in the capture phase, so an arrow during the tour can
 * never nudge a booking that happened to be selected on the Board.
 */

/** Where the tour came from decides what stopping means. */
export type TourSource = 'welcome' | 'manual'

export function Tour({
  steps,
  source,
  field,
  onFinish,
  onLater,
  onNever,
  onStop,
}: {
  steps: TourStep[]
  source: TourSource
  field: boolean
  onFinish: () => void
  onLater: () => void
  onNever: () => void
  onStop: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [index, setIndex] = useState(0)
  const [asking, setAsking] = useState(false)
  const step = steps[index]
  const last = index === steps.length - 1
  const target = useTarget(step?.target)
  const next = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (step?.route && location.pathname !== step.route) navigate(step.route)
    // Only when the step changes: the person is not allowed to wander off
    // mid-step, so the route follows the step and not the other way round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id])

  useEffect(() => {
    if (!asking) next.current?.focus()
  }, [index, asking, target.status])

  const stop = () => {
    // Taken from Settings, stopping is only stopping. From the welcome it is a
    // choice about next time, so it is asked rather than guessed.
    if (source === 'manual') onStop()
    else setAsking(true)
  }

  const go = (delta: number) => {
    setAsking(false)
    if (delta > 0 && last) onFinish()
    else setIndex((was) => Math.min(Math.max(was + delta, 0), steps.length - 1))
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (asking) setAsking(false)
        else stop()
        return
      }
      if (asking) return
      if (event.key === 'ArrowRight' || (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement))) {
        event.preventDefault()
        event.stopPropagation()
        go(1)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        event.stopPropagation()
        go(-1)
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === '[' || event.key === ']') {
        // Swallowed: the Board under the tour moves bookings on these.
        event.stopPropagation()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  if (!step) return null
  // Hold the card back for the moment the target is being looked for, so it
  // does not flash in the middle and then jump to the rail.
  if (step.target && target.status === 'looking') return null

  return (
    <SpotlightFrame box={target.box} field={field} labelledBy="guide-tour-title">
      <div className="flex items-center gap-1 px-4 pt-3.5" aria-hidden="true">
        {steps.map((entry, at) => (
          <span
            key={entry.id}
            className={cn(
              'h-1 flex-1 rounded-[1px] transition-colors',
              at < index ? 'bg-ink-900' : at === index ? 'bg-brand-500' : 'bg-ink-200',
            )}
          />
        ))}
      </div>

      {asking ? (
        <div className="px-4 pt-3 pb-4">
          <p id="guide-tour-title" className="font-display text-[17px] leading-snug font-extrabold text-ink-900">
            Stop the tour?
          </p>
          <p className="mt-1 text-sm text-ink-600">
            You can take it again any time from Settings → Your account
            {field ? ', or Me' : ''}.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <Button variant="primary" onClick={onLater} ref={next}>
              Show it next time
            </Button>
            <Button onClick={onNever}>Don’t show this again</Button>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Back to the tour
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3 px-4 pt-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] font-bold tracking-wider text-ink-500 uppercase tabular-nums">
                {index + 1} of {steps.length}
              </p>
              <h2
                id="guide-tour-title"
                className="mt-0.5 font-display text-[17px] leading-snug font-extrabold text-ink-900"
              >
                {step.title}
              </h2>
            </div>
            <button
              type="button"
              onClick={stop}
              aria-label="Stop the tour"
              className="-mt-0.5 -mr-1.5 grid size-8 shrink-0 place-items-center rounded-md text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
            >
              <Icon name="close" className="size-4" />
            </button>
          </div>
          <p className="px-4 pt-1.5 text-sm leading-relaxed text-ink-600" aria-live="polite">
            {step.body}
          </p>

          <div className="mt-4 flex items-center gap-2 border-t border-ink-100 px-4 py-3">
            <button
              type="button"
              onClick={stop}
              className="mr-auto rounded-md px-1 py-1 text-xs font-medium text-ink-500 hover:text-ink-900"
            >
              {source === 'manual' ? 'End tour' : 'Skip tour'}
            </button>
            {index > 0 ? (
              <Button size="sm" onClick={() => go(-1)}>
                Back
              </Button>
            ) : null}
            <Button ref={next} size="sm" variant="primary" onClick={() => go(1)}>
              {last ? 'Finish' : 'Next'}
              {last ? null : <Icon name="forward" className="size-4" />}
            </Button>
          </div>
        </>
      )}
    </SpotlightFrame>
  )
}
